// TelcoTrack — formatting and aggregation helpers for materials panel.

import type * as ExcelJS from 'exceljs';
import { formatMaterialQuantityLabel } from '@core/shared-bridge';
import type { MaterialsUsedPageItem } from '../../core/services/object.service';
import type { MaterialAggExportRow, MaterialPanelRow } from './materials-panel.types';
import { applyExcelTableBorders, styleExcelHeaderRow } from './xlsx-export-style.util';

/** Počet — bez úvodních nul; v kódu formátu je desetinná tečka (Excel zobrazí čárku dle locale). */
const SUMMARY_EXPORT_QTY_FMT = '# ##0.##';
const SUMMARY_EXPORT_MONEY_FMT = '# ##0,00" Kč"';
const SUMMARY_EXPORT_TABLE_COL_COUNT = 6;

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundQuantity(value: number): number {
  return Math.round(value * 10000) / 10000;
}

function parseSummaryExportNumber(value: ExcelJS.CellValue): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const n = Number(trimmed.replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function setSummaryExportNumericCell(cell: ExcelJS.Cell, value: number, numFmt: string): void {
  cell.value = value;
  cell.numFmt = numFmt;
}

/** Vážená jednotková cena z agregovaného množství a částky (lineTotal / quantity). */
export function resolveMaterialAggUnitPrice(row: Pick<MaterialAggExportRow, 'quantity' | 'lineTotal'>): number | '' {
  const qty = Number(row.quantity) || 0;
  const total = Number(row.lineTotal) || 0;
  if (qty <= 0 || total <= 0) return '';
  return roundMoney(total / qty);
}

export const MATERIALS_SUMMARY_EXPORT_COL_WIDTHS = [
  { width: 12 },
  { width: 42 },
  { width: 12 },
  { width: 10 },
  { width: 16 },
  { width: 16 },
] as const;

/** 1-based index řádku sloupce hlavičky v souhrnném exportu (Od/Do = 1, prázdný = 2, hlavička = 3). */
export const MATERIALS_SUMMARY_EXPORT_HEADER_ROW = 3;

export function mapMaterialsPageItem(it: MaterialsUsedPageItem): MaterialPanelRow {
  return {
    usageId: it.usageId ?? '',
    usedAt: it.usedAt ?? '',
    changedBy: it.changedBy ?? '',
    polygonId: it.polygonId ?? null,
    objectId: it.objectId ?? '',
    objectType: it.objectType ?? '',
    objectMetadata: it.objectMetadata ?? null,
    objectStatus: it.objectStatus ?? null,
    sapCode: it.sapCode ?? '',
    name: it.name ?? '',
    quantity: Number(it.quantity) || 0,
    unit: it.unit ?? null,
    price: it.price ?? null,
    lineTotal: it.lineTotal ?? null,
    mainCategory: it.mainCategory ?? null,
    subCategory: it.subCategory ?? null,
    branch1: it.branch1 ?? null,
    branch2: it.branch2 ?? null,
    branch3: it.branch3 ?? null,
    branch4: it.branch4 ?? null,
    supplier: it.supplier ?? null,
  };
}

export function formatMaterialDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return new Intl.DateTimeFormat('cs-CZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function formatMaterialQuantity(qty: number, unit: string | null): string {
  // Pack-aware: `3` + `10 ks` → `3 ks` (ne `3 10 ks`).
  return formatMaterialQuantityLabel(qty, unit, 'usage');
}

/**
 * Identita metrového kusu (role / WI) pro UI skladu — bez metráže (tu drží samostatný sloupec).
 * Preferuje pozici, pak krátké ID; „Role N“ jen při více kusech (ne „kus 1“).
 */
export function formatWarehouseMeterPieceIdentity(
  piece: {
    index: number;
    warehouseItemId?: string | null;
    positionName?: string | null;
    /** Uživatelské označení (smotek A, číslo, barva). */
    label?: string | null;
  },
  pieceCount: number,
): string | null {
  const tag = String(piece.label ?? '').trim();
  if (tag) return tag;

  const pos = String(piece.positionName ?? '').trim();
  if (pos) return pos;

  const id = String(piece.warehouseItemId ?? '').trim();
  if (id) {
    return id.length > 10 ? `ID …${id.slice(-6)}` : `ID ${id}`;
  }

  if (pieceCount > 1) return `Role ${piece.index}`;
  return null;
}

/**
 * If `title` starts with `productName` plus a separator (or equals it),
 * return only leftover extras so the catalog name is not shown twice.
 */
export function stripDuplicateProductNamePrefix(
  title: string | null | undefined,
  productName: string | null | undefined,
): string {
  const id = String(title ?? '').trim();
  const name = String(productName ?? '').trim();
  if (!id || !name) return id;
  if (id.localeCompare(name, undefined, { sensitivity: 'accent' }) === 0) return '';
  if (id.length <= name.length) return id;
  if (id.slice(0, name.length).localeCompare(name, undefined, { sensitivity: 'accent' }) !== 0) {
    return id;
  }
  const rest = id.slice(name.length);
  const first = rest[0];
  const isSep = first != null && (/\s/.test(first) || first === '·' || first === '-' || first === '–' || first === '—');
  if (!isSep) return id;
  return rest.replace(/^[\s·\-–—]+/, '').trim();
}

export function formatMaterialPrice(value: number | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return (
    new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' Kč'
  );
}

export function buildMaterialsExportAggregated(rows: readonly MaterialPanelRow[]): MaterialAggExportRow[] {
  const map = new Map<string, MaterialAggExportRow>();
  for (const r of rows) {
    const key = (r.sapCode || '').trim() || '—';
    const cur = map.get(key) ?? {
      sapCode: key,
      name: r.name || key,
      quantity: 0,
      lineTotal: 0,
      unit: r.unit,
    };
    cur.quantity += Number(r.quantity) || 0;
    cur.lineTotal += Number(r.lineTotal) || 0;
    if (!cur.name && r.name) cur.name = r.name;
    if (!cur.unit && r.unit) cur.unit = r.unit;
    map.set(key, cur);
  }
  return Array.from(map.values()).sort((a, b) => b.lineTotal - a.lineTotal);
}

/** ISO `YYYY-MM-DD` → `dd.mm.yyyy` (or pass-through). */
export function isoDateToCsDateOnly(value: string | null | undefined): string {
  const text = (value ?? '').trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  return iso ? `${iso[3]}.${iso[2]}.${iso[1]}` : text;
}

/**
 * Popisky Od–Do pro hlavičku souhrnného Excel exportu materiálů.
 * Preferuje filtr měsíců (první/poslední den rozsahu), jinak hodnoty Od/Do z UI.
 */
export function resolveMaterialsExportPeriodLabels(args: {
  selectedMonths?: readonly string[];
  dateFrom?: string | null;
  dateTo?: string | null;
}): { from: string; to: string } {
  const months = (args.selectedMonths ?? []).map((m) => m.trim()).filter(Boolean).sort();
  if (months.length > 0) {
    const first = months[0]!;
    const last = months[months.length - 1]!;
    const [fy, fm] = first.split('-').map(Number);
    const [ly, lm] = last.split('-').map(Number);
    if (Number.isFinite(fy) && Number.isFinite(fm) && Number.isFinite(ly) && Number.isFinite(lm)) {
      const lastDay = new Date(ly, lm, 0).getDate();
      return {
        from: `01.${String(fm).padStart(2, '0')}.${fy}`,
        to: `${String(lastDay).padStart(2, '0')}.${String(lm).padStart(2, '0')}.${ly}`,
      };
    }
  }
  const fromRaw = (args.dateFrom ?? '').trim();
  const toRaw = (args.dateTo ?? '').trim();
  return {
    from: fromRaw ? isoDateToCsDateOnly(fromRaw) : '—',
    to: toRaw ? isoDateToCsDateOnly(toRaw) : '—',
  };
}

/**
 * Souhrnný Excel export materiálů: hlavička Od–Do + SAP / název / počet / jednotka / ceny + řádek CELKEM.
 * Detailní export se sestavuje jinde a zůstává beze změny.
 */
export function buildMaterialsSummaryExportAoA(
  rows: readonly MaterialPanelRow[],
  period: { from: string; to: string },
): (string | number)[][] {
  const aoa: (string | number)[][] = [
    ['Od', period.from, 'Do', period.to],
    [],
    ['SAP', 'Název materiálu', 'Počet', 'Jednotka', 'Jednotková cena', 'Celková cena'],
  ];
  let grandTotal = 0;
  for (const r of buildMaterialsExportAggregated(rows)) {
    const quantity = roundQuantity(Number(r.quantity) || 0);
    const lineTotal = roundMoney(r.lineTotal);
    grandTotal += lineTotal;
    aoa.push([
      r.sapCode,
      r.name,
      quantity,
      r.unit ?? 'ks',
      resolveMaterialAggUnitPrice({ quantity, lineTotal }),
      lineTotal,
    ]);
  }
  aoa.push(['CELKEM', '', '', '', '', roundMoney(grandTotal)]);
  return aoa;
}

/** ExcelJS styly pro souhrnný export materiálů (tučná hlavička a CELKEM, číselné formáty, ohraničení tabulky). */
export function styleMaterialsSummaryExportSheet(ws: ExcelJS.Worksheet, rowCount: number): void {
  ws.columns = [...MATERIALS_SUMMARY_EXPORT_COL_WIDTHS];

  const headerRow = MATERIALS_SUMMARY_EXPORT_HEADER_ROW;
  styleExcelHeaderRow(ws, headerRow, SUMMARY_EXPORT_TABLE_COL_COUNT);

  const dataStartRow = headerRow + 1;
  const totalRow = rowCount;
  for (let row = dataStartRow; row < totalRow; row++) {
    const qtyCell = ws.getCell(row, 3);
    const qty = parseSummaryExportNumber(qtyCell.value);
    if (qty != null) {
      setSummaryExportNumericCell(qtyCell, roundQuantity(qty), SUMMARY_EXPORT_QTY_FMT);
    }

    const unitPriceCell = ws.getCell(row, 5);
    const unitPrice = parseSummaryExportNumber(unitPriceCell.value);
    if (unitPrice != null) {
      setSummaryExportNumericCell(unitPriceCell, roundMoney(unitPrice), SUMMARY_EXPORT_MONEY_FMT);
    } else {
      unitPriceCell.numFmt = SUMMARY_EXPORT_MONEY_FMT;
    }

    const lineTotalCell = ws.getCell(row, 6);
    const lineTotal = parseSummaryExportNumber(lineTotalCell.value);
    if (lineTotal != null) {
      setSummaryExportNumericCell(lineTotalCell, roundMoney(lineTotal), SUMMARY_EXPORT_MONEY_FMT);
    }
  }

  if (totalRow > headerRow) {
    for (let col = 1; col <= SUMMARY_EXPORT_TABLE_COL_COUNT; col++) {
      const cell = ws.getCell(totalRow, col);
      cell.font = { ...(cell.font ?? {}), bold: true };
    }
    const grandCell = ws.getCell(totalRow, 6);
    const grandTotal = parseSummaryExportNumber(grandCell.value);
    if (grandTotal != null) {
      setSummaryExportNumericCell(grandCell, roundMoney(grandTotal), SUMMARY_EXPORT_MONEY_FMT);
    } else {
      grandCell.numFmt = SUMMARY_EXPORT_MONEY_FMT;
    }
  }

  if (totalRow >= headerRow) {
    applyExcelTableBorders(ws, headerRow, totalRow, 1, SUMMARY_EXPORT_TABLE_COL_COUNT);
  }
}
